import _ from "lodash";
import client from "lib/apolloClient/client";
import update from "immutability-helper";

import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  goToPlatformHomeChildRoute,
  goToAdminChildRoute,
} from "modules/NavigationModule";
import { getOrganizationId } from "modules/CommonSelectors";

import {
  getIncidentFeedFromCache,
  writeIncidentFeedToCache,
} from "BehaviourManagement/modules/BehaviourManagementGraphqlHelpers";
import { getActivityLogQuery } from "Incident/modules/IncidentQueries";

import { getBehaviourNomenclatureFromCache } from "BehaviourConfiguration/modules/BehaviourConfigurationGraphqlHelpers";

import {
  updateIncidentMutation,
  createBehaviourIncidentMutation,
  deleteBehaviourIncidentMutation,
  discardBehaviourIncidentMutation,
  duplicateBehaviourIncidentMutation,
} from "./IncidentMutations";

import { getIsCurriculumBoundaryApplicable } from "BehaviourManagement/modules/Utils";
import {
  getIncidentDetailsFromCache,
  writeIncidentDetailsToCache,
} from "./IncidentGraphqlHelpers";
import { getIncidentDetailsQuery } from "./IncidentQueries";

export const NAME = "incident";
export const UPDATE_INCIDENT = "UPDATE_INCIDENT" + " " + NAME;
export const TOGGLE_PUBLISH_MODAL = "TOGGLE_PUBLISH_MODAL" + " " + NAME;
export const TOGGLE_RESOLVE_MODAL = "TOGGLE_RESOLVE_MODAL" + " " + NAME;
export const TOGGLE_REVERT_TO_DRAFT_MODAL =
  "TOGGLE_REVERT_TO_DRAFT_MODAL" + " " + NAME;
export const TOGGLE_REVERT_TO_PUBLISH_MODAL =
  "TOGGLE_REVERT_TO_PUBLISH_MODAL" + " " + NAME;
export const TOGGLE_CATEGORY_MODAL = "TOGGLE_CATEGORY_MODAL" + " " + NAME;
export const UPDATE_SELECTED_CATEGORY = "UPDATE_SELECTED_CATEGORY" + " " + NAME;
export const UPDATE_SELECTED_SEVERITY = "UPDATE_SELECTED_SEVERITY" + " " + NAME;
export const UPDATE_SELECTED_SUBCATEGORY =
  "UPDATE_SELECTED_SUBCATEGORY" + " " + NAME;
export const UPDATE_CATEGORY_CONFIRMATION_MODAL =
  "UPDATE_CATEGORY_CONFIRMATION_MODAL" + " " + NAME;
export const TOGGLE_ACTIVITY_LOG = "TOGGLE_ACTIVITY_LOG" + " " + NAME;
export const UPDATE_STUDENT_SEARCH_TEXT =
  "UPDATE_STUDENT_SEARCH_TEXT" + " " + NAME;
export const UPDATE_STAFF_SEARCH_TEXT = "UPDATE_STAFF_SEARCH_TEXT" + " " + NAME;

export const navigateToIncident = ({
  incidentId,
  isOpenInNewTab = false,
  loginActiveTab,
  curriculumProgramId,
  academicYearId,
}) => {
  return async dispatch => {
    const route = `incident/${incidentId}`;

    if (loginActiveTab === "admin") {
      dispatch(goToAdminChildRoute({ route, isOpenInNewTab }));
      return;
    }
    dispatch(
      goToPlatformHomeChildRoute({
        route,
        isOpenInNewTab,
        curriculumProgramId,
        academicYearId,
      })
    );
  };
};

export const createIncident = ({
  variables,
  refetchQueries = [],
  loginActiveTab,
}) => {
  return async dispatch => {
    try {
      const isCurriculumBoundaryApplicable = getIsCurriculumBoundaryApplicable();

      const response = await client.mutate({
        mutation: createBehaviourIncidentMutation,
        variables,
        refetchQueries,
      });

      const incident = _.get(
        response,
        "data.platform.createBehaviourIncident",
        {}
      );

      if (_.isEmpty(incident)) {
        throw new Error();
      }

      writeIncidentDetailsToCache({
        variables: {
          id: incident.id,
          includeUserCurriculums: isCurriculumBoundaryApplicable,
        },
        data: { node: incident },
      });

      dispatch(
        navigateToIncident({
          incidentId: incident.id,
          loginActiveTab,
        })
      );
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const deleteIncident = ({
  incidentId,
  refetchQueries = [],
  variables,
}) => {
  return async dispatch => {
    const cacheData = getIncidentFeedFromCache(variables);

    try {
      const organizationId = getOrganizationId();
      const nomenclatureConstants = getBehaviourNomenclatureFromCache({
        organizationId,
      });

      const response = await client.mutate({
        mutation: deleteBehaviourIncidentMutation,
        variables: {
          input: {
            id: incidentId,
          },
        },
        refetchQueries,
        awaitRefetchQueries: true,
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            __typename: "PlatformMutations",
            deleteBehaviourIncident: true,
          },
        },
        update: (
          cache,
          {
            data: {
              platform: { deleteBehaviourIncident },
            },
          }
        ) => {
          if (deleteBehaviourIncident) {
            const updatedFeedList = _.filter(
              _.get(cacheData, "behaviourIncidentFeed.edges", []),
              feedItem => feedItem.node.id !== incidentId
            );

            const totalCount = _.get(
              cacheData,
              "behaviourIncidentFeed.totalCount"
            );

            setTimeout(() => {
              writeIncidentFeedToCache({
                variables,
                data: {
                  node: update(cacheData, {
                    behaviourIncidentFeed: {
                      totalCount: { $set: totalCount - 1 },
                      edges: { $set: updatedFeedList },
                    },
                  }),
                },
              });
            });
          }
        },
      });

      const isSuccess = _.get(
        response,
        "data.platform.deleteBehaviourIncident",
        false
      );

      if (isSuccess) {
        dispatch(
          setToastMsg({
            type: "success",
            msg: "toastMsgs:entity_deleted_successfully",
            locale_params: [
              {
                key: "entity",
                value: nomenclatureConstants.INCIDENT,
                isPlainText: true,
              },
            ],
          })
        );
      }
    } catch (e) {
      setTimeout(() => {
        writeIncidentFeedToCache({
          variables,
          data: {
            node: cacheData,
          },
        });
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const duplicateIncident = ({ incidentId }) => {
  return async dispatch => {
    try {
      const organizationId = getOrganizationId();
      const nomenclatureConstants = getBehaviourNomenclatureFromCache({
        organizationId,
      });

      const response = await client.mutate({
        mutation: duplicateBehaviourIncidentMutation,
        variables: {
          input: {
            id: incidentId,
          },
        },
      });

      const duplicateIncidentId = _.get(
        response,
        "data.platform.duplicateBehaviourIncident.id"
      );

      if (!duplicateIncidentId) {
        throw new Error();
      }

      dispatch(
        setToastMsg({
          msg: "toastMsgs:entity_duplicated_successfully",
          locale_params: [
            {
              key: "entity",
              value: nomenclatureConstants.INCIDENT,
              isPlainText: true,
            },
          ],
          type: "success",
        })
      );

      return duplicateIncidentId;
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

////// ----- ACTIONS ----- \\\\\\

export const togglePublishModal = data => {
  return { data, type: TOGGLE_PUBLISH_MODAL };
};

export const toggleResolveModal = data => {
  return { data, type: TOGGLE_RESOLVE_MODAL };
};

export const toggleRevertToDraftModal = data => {
  return { data, type: TOGGLE_REVERT_TO_DRAFT_MODAL };
};

export const toggleRevertToPublishModal = data => {
  return { data, type: TOGGLE_REVERT_TO_PUBLISH_MODAL };
};

export const toggleCategoryModal = data => {
  return { data, type: TOGGLE_CATEGORY_MODAL };
};

export const updateSelectedCategory = data => {
  return { data, type: UPDATE_SELECTED_CATEGORY };
};

export const updateSelectedSeverity = data => {
  return { data, type: UPDATE_SELECTED_SEVERITY };
};

export const updateSelectedSubCategory = data => {
  return { data, type: UPDATE_SELECTED_SUBCATEGORY };
};

export const updateCategoryConfirmationModal = data => {
  return { data, type: UPDATE_CATEGORY_CONFIRMATION_MODAL };
};

export const toggleActivityLog = data => {
  return { data, type: TOGGLE_ACTIVITY_LOG };
};

export const updateStudentSearchText = data => {
  return { data, type: UPDATE_STUDENT_SEARCH_TEXT };
};

export const updateStaffSearchText = data => {
  return { data, type: UPDATE_STAFF_SEARCH_TEXT };
};

export const getIncidentActivityLogVariables = _.memoize(({ id }) => {
  return { id, input: { first: 15 } };
}, JSON.stringify);

export const updateIncidentDetails = ({ id, key, data, updatedDataObj }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const isActivityLogOpen = _.get(state, "incident.isActivityLogOpen", false);
    const isCurriculumBoundaryApplicable = getIsCurriculumBoundaryApplicable();
    // call mutation with incident id and updated data obj
    const updatedIncidentData = {
      id,
      ...data,
    };

    const incidentData = getIncidentDetailsFromCache({
      id,
      includeUserCurriculums: isCurriculumBoundaryApplicable,
    });

    try {
      // TODO: Remove this after optimistic response implementation of actions field
      if (key !== "ACTIONS") {
        writeIncidentDetailsToCache({
          variables: {
            id,
            includeUserCurriculums: isCurriculumBoundaryApplicable,
          },
          data: {
            node: {
              ...incidentData,
              ...updatedDataObj,
            },
          },
        });
      }

      const result = await client.mutate({
        mutation: updateIncidentMutation,
        variables: {
          input: updatedIncidentData,
        },
        refetchQueries: isActivityLogOpen
          ? [
              {
                query: getActivityLogQuery,
                variables: getIncidentActivityLogVariables({ id }),
              },
            ]
          : [],
      });
      return result;
    } catch (err) {
      // revert back to previous state in case of any error
      writeIncidentDetailsToCache({
        variables: {
          id,
          includeUserCurriculums: isCurriculumBoundaryApplicable,
        },
        data: {
          node: incidentData,
        },
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const updateIncidentDetailsV2 = ({ id, input }) => {
  return async dispatch => {
    const isCurriculumBoundaryApplicable = getIsCurriculumBoundaryApplicable();

    try {
      const result = await client.mutate({
        mutation: updateIncidentMutation,
        variables: {
          input: {
            id,
            ...input,
          },
        },
        refetchQueries: [
          {
            query: getIncidentDetailsQuery,
            variables: {
              id,
              includeUserCurriculums: isCurriculumBoundaryApplicable,
            },
          },
        ],
        awaitRefetchQueries: true,
      });

      return result;
    } catch (err) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

// TODO: remove this fn after the common createAttachment thunk is verified

export const discardIncident = ({ incidentId }) => {
  return async dispatch => {
    try {
      await client.mutate({
        mutation: discardBehaviourIncidentMutation,
        variables: {
          input: {
            id: incidentId,
          },
        },
      });
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

////// ----- REDUCERS ----- \\\\\\
const REDUCER_HANDLERS = {
  [UPDATE_INCIDENT]: (state, action) => {
    const params = action.data;

    Object.keys(params).map(key => {
      state = update(state, {
        dummyIncident: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },
  [TOGGLE_PUBLISH_MODAL]: (state, action) => {
    return update(state, {
      stateChangeModal: {
        showPublishModal: { $set: action.data },
      },
    });
  },
  [TOGGLE_RESOLVE_MODAL]: (state, action) => {
    return update(state, {
      stateChangeModal: {
        showResolveModal: { $set: action.data },
      },
    });
  },
  [TOGGLE_REVERT_TO_DRAFT_MODAL]: (state, action) => {
    return update(state, {
      stateChangeModal: {
        showRevertToDraftModal: { $set: action.data },
      },
    });
  },
  [TOGGLE_REVERT_TO_PUBLISH_MODAL]: (state, action) => {
    return update(state, {
      stateChangeModal: {
        showRevertToPublishModal: { $set: action.data },
      },
    });
  },
  [TOGGLE_CATEGORY_MODAL]: (state, action) => {
    return update(state, {
      categoryModal: {
        isVisible: { $set: action.data },
      },
    });
  },
  [UPDATE_SELECTED_CATEGORY]: (state, action) => {
    return update(state, {
      categoryModal: {
        selectedCategory: { $set: action.data },
      },
    });
  },
  [UPDATE_SELECTED_SEVERITY]: (state, action) => {
    return update(state, {
      categoryModal: {
        selectedSeverity: { $set: action.data },
      },
    });
  },
  [UPDATE_SELECTED_SUBCATEGORY]: (state, action) => {
    return update(state, {
      categoryModal: {
        selectedSubCategory: { $set: action.data },
      },
    });
  },
  [UPDATE_CATEGORY_CONFIRMATION_MODAL]: (state, action) => {
    const params = action.data;

    Object.keys(params).map(key => {
      state = update(state, {
        categoryModal: {
          confirmationModal: { [key]: { $set: params[key] } },
        },
      });
    });

    return state;
  },
  [TOGGLE_ACTIVITY_LOG]: (state, action) => {
    return update(state, {
      isActivityLogOpen: { $set: action.data },
    });
  },
  [UPDATE_STUDENT_SEARCH_TEXT]: (state, action) => {
    return update(state, {
      studentSearchText: { $set: action.data },
    });
  },
  [UPDATE_STAFF_SEARCH_TEXT]: (state, action) => {
    return update(state, {
      staffSearchText: { $set: action.data },
    });
  },
};

const initialState = {
  stateChangeModal: {
    showPublishModal: false,
    showResolveModal: false,
    showRevertToDraftModal: false,
    showRevertToPublishModal: false,
  },
  categoryModal: {
    isVisible: false,
    selectedCategory: null,
    selectedSeverity: null,
    selectedSubCategory: null,
    confirmationModal: {
      isVisible: false,
      selectedOption: null,
    },
  },
  isActivityLogOpen: false,
  studentSearchText: "",
  staffSearchText: "",
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
